"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  FlagImage,
  defaultCountries,
  parseCountry,
  usePhoneInput,
  type CountryIso2,
} from "react-international-phone";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { normalizeE164Phone } from "@repo/utils";

import { cn } from "@/lib/utils";
import { shouldApplyPhoneCountryFallback } from "@/components/ui/phone-country";

const countries = defaultCountries.map((country) => parseCountry(country));
const countryIsoSet = new Set(countries.map((country) => country.iso2));

interface PhoneInputProps {
  id?: string;
  className?: string;
  controlClassName?: string;
  inputClassName?: string;
  countryButtonClassName?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  defaultCountry?: string;
  rawValue?: string;
  helperText?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: (value: { countryCode: string; phoneNumber: string }) => void;
}

function normalizePhoneValue(rawValue?: string): string {
  if (!rawValue) return "";
  const trimmed = rawValue.trim();
  if (!trimmed) return "";

  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";

  return trimmed.startsWith("+") ? `+${digits}` : digits;
}

function getDefaultCountryIsoFromProp(defaultCountry?: string): CountryIso2 {
  if (!defaultCountry) return "us";

  const lowered = defaultCountry.trim().toLowerCase();
  const byIso = countries.find((country) => country.iso2 === lowered);
  if (byIso) return byIso.iso2;

  const byName = countries.find(
    (country) => country.name.toLowerCase() === lowered,
  );
  if (byName) return byName.iso2;

  return "us";
}

function getCountryFromLocale(): CountryIso2 | null {
  if (typeof navigator === "undefined") return null;

  const locale = navigator.language || "";
  const parts = locale.split("-");
  const region = parts[parts.length - 1]?.toLowerCase();
  if (!region || !countryIsoSet.has(region as CountryIso2)) return null;
  return region as CountryIso2;
}

function PhoneCountryList({
  search,
  onSearch,
  countries: countryOptions,
  selectedIso,
  onSelect,
}: {
  search: string;
  onSearch: (value: string) => void;
  countries: typeof countries;
  selectedIso: string;
  onSelect: (iso: CountryIso2) => void;
}) {
  return (
    <Command shouldFilter={false}>
      <CommandInput
        value={search}
        onValueChange={onSearch}
        placeholder="Search country or code..."
        className="border-0 ring-0 focus-visible:ring-0"
      />
      <CommandList className="max-h-[50dvh]">
        <CommandEmpty>No country found.</CommandEmpty>
        <CommandGroup>
          {countryOptions.map((countryOption) => (
            <CommandItem
              key={countryOption.iso2}
              value={`${countryOption.name} +${countryOption.dialCode}`}
              onSelect={() => onSelect(countryOption.iso2)}
              className="flex min-h-11 items-center justify-between gap-2"
            >
              <span className="flex min-w-0 items-center gap-2">
                <FlagImage
                  iso2={countryOption.iso2}
                  className="size-4 rounded-[2px]"
                />
                <span className="truncate">{countryOption.name}</span>
              </span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                +{countryOption.dialCode}
                {selectedIso === countryOption.iso2 && (
                  <Check className="size-3.5 text-foreground" />
                )}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function PhoneInputControl({
  id = "phone",
  className,
  controlClassName,
  inputClassName,
  countryButtonClassName,
  required = false,
  label,
  placeholder = "Enter phone number",
  defaultCountry = "United States",
  rawValue,
  helperText = "Used only for account and inquiry communication. No spam.",
  onBlur,
  onChange,
}: PhoneInputProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [compact, setCompact] = React.useState(false);
  const userSelectedRef = React.useRef(false);
  const defaultCountryIso = React.useMemo(
    () => getDefaultCountryIsoFromProp(defaultCountry),
    [defaultCountry],
  );
  const guessedCountryIso = defaultCountryIso;

  const normalizedRawValue = React.useMemo(
    () => normalizePhoneValue(rawValue),
    [rawValue],
  );

  const { country, setCountry, inputValue, handlePhoneValueChange, inputRef } =
    usePhoneInput({
      defaultCountry: guessedCountryIso,
      value: normalizedRawValue,
      countries: defaultCountries,
      disableDialCodeAndPrefix: true,
      onChange: ({ phone, country }) => {
        const phoneDigits = phone.replace(/\D/g, "");
        const dialDigits = country.dialCode.replace(/\D/g, "");
        const nationalNumber = phoneDigits.startsWith(dialDigits)
          ? phoneDigits.slice(dialDigits.length)
          : phoneDigits;
        const normalized = normalizeE164Phone(`+${dialDigits}${nationalNumber}`);
        const storedNational =
          normalized?.startsWith(`+${dialDigits}`)
            ? normalized.slice(dialDigits.length + 1)
            : nationalNumber;

        onChange?.({
          countryCode: `+${country.dialCode}`,
          phoneNumber: storedNational,
        });
      },
    });

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const apply = () => setCompact(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  React.useEffect(() => {
    if (
      !shouldApplyPhoneCountryFallback({
        userSelected: userSelectedRef.current,
        hasNationalNumber: Boolean(normalizedRawValue),
      })
    ) {
      return;
    }
    if (country.iso2 === guessedCountryIso) return;
    setCountry(guessedCountryIso, { focusOnInput: false });
  }, [country.iso2, guessedCountryIso, normalizedRawValue, setCountry]);

  const filteredCountries = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return countries;

    return countries.filter((country) => {
      return (
        country.name.toLowerCase().includes(query) ||
        country.iso2.toLowerCase().includes(query) ||
        `+${country.dialCode}`.includes(query)
      );
    });
  }, [search]);

  function handleCountrySelect(countryIso: CountryIso2) {
    userSelectedRef.current = true;
    setCountry(countryIso, { focusOnInput: true });
    setOpen(false);
    setSearch("");
  }

  function handleCountryPopoverToggle(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) setSearch("");
  }

  const countryList = (
    <PhoneCountryList
      search={search}
      onSearch={setSearch}
      countries={filteredCountries}
      selectedIso={country.iso2}
      onSelect={handleCountrySelect}
    />
  );

  const countryButton = (
    <Button
      type="button"
      variant="ghost"
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label={`Change country, currently ${country.name}`}
      className={cn(
        "h-12 min-h-11 min-w-[96px] rounded-r-none border-r px-3.5 text-base text-foreground hover:bg-muted/40",
        countryButtonClassName,
      )}
      onClick={compact ? () => handleCountryPopoverToggle(!open) : undefined}
    >
      <FlagImage iso2={country.iso2} className="size-4 rounded-[2px]" />
      <span className="ml-1 text-sm text-muted-foreground">
        +{country.dialCode}
      </span>
      <ChevronsUpDown className="ml-1.5 size-3.5 opacity-60" />
    </Button>
  );

  return (
    <div className={cn("grid gap-2", className)}>
      {label && (
        <Label htmlFor={id} className="text-sm font-medium text-foreground">
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      <div
        className={cn(
          "flex items-center rounded-md border border-input bg-transparent focus-within:ring-2 focus-within:ring-ring/30",
          controlClassName,
        )}
      >
        {compact ? (
          countryButton
        ) : (
          <Popover open={open} onOpenChange={handleCountryPopoverToggle}>
            <PopoverTrigger asChild>{countryButton}</PopoverTrigger>
            <PopoverContent className="z-[1202] w-[280px] p-0">
              {countryList}
            </PopoverContent>
          </Popover>
        )}
        {compact ? (
          <Dialog open={open} onOpenChange={handleCountryPopoverToggle}>
            <DialogContent className="bottom-0 top-auto max-h-[85dvh] w-full max-w-none translate-y-0 rounded-b-none sm:rounded-b-none">
              <DialogHeader>
                <DialogTitle>Choose a country</DialogTitle>
              </DialogHeader>
              {countryList}
            </DialogContent>
          </Dialog>
        ) : null}

        <Input
          id={id}
          ref={inputRef}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required={required}
          value={inputValue}
          onChange={handlePhoneValueChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={cn(
            "h-12 rounded-l-none border-0 text-base shadow-none focus-visible:ring-0 md:text-base",
            inputClassName,
          )}
        />
      </div>

      {helperText ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  );
}

export function PhoneInput(props: PhoneInputProps) {
  const {
    defaultCountry,
  } = props;
  const defaultCountryIso = React.useMemo(
    () => getDefaultCountryIsoFromProp(defaultCountry),
    [defaultCountry],
  );
  const [guessedCountryIso, setGuessedCountryIso] =
    React.useState<CountryIso2>(defaultCountryIso);

  React.useEffect(() => {
    if (defaultCountry?.trim()) {
      setGuessedCountryIso(defaultCountryIso);
      return;
    }
    setGuessedCountryIso(getCountryFromLocale() ?? defaultCountryIso);
  }, [defaultCountry, defaultCountryIso]);

  React.useEffect(() => {
    if (defaultCountry?.trim()) return;
    const abortController = new AbortController();
    let isUnmounted = false;

    async function detectCountryFromIp() {
      try {
        const cached = sessionStorage.getItem("venturu-phone-country");
        if (cached && countryIsoSet.has(cached as CountryIso2)) {
          setGuessedCountryIso(cached as CountryIso2);
          return;
        }
        const response = await fetch("/api/geo/country", {
          signal: abortController.signal,
          cache: "no-store",
        });
        if (!response.ok) return;
        const json = (await response.json()) as {
          countryCode?: string | null;
        };
        const iso = json.countryCode?.toLowerCase();
        if (!iso || !countryIsoSet.has(iso as CountryIso2)) return;
        if (isUnmounted) return;
        sessionStorage.setItem("venturu-phone-country", iso);
        setGuessedCountryIso(iso as CountryIso2);
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    }

    void detectCountryFromIp();
    return () => {
      isUnmounted = true;
      abortController.abort();
    };
  }, [defaultCountry]);

  return (
    <PhoneInputControl
      {...props}
      defaultCountry={guessedCountryIso}
    />
  );
}
